import { useMemo } from "react";

import {
  CalendarMonth as CalendarIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  Inventory2 as ProductIcon,
  Search as SearchIcon,
  Store as StoreIcon,
  Tune as TuneIcon,
  VisibilityOff as VisibilityOffIcon,
  Warehouse as WarehouseIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import PropTypes from "prop-types";

import { UserOutletsSelect } from "@/features/outlets";
import { ProductCategorySelect } from "@/features/product-categories";
import { ProductSelect } from "@/features/products";
import { formatDate } from "@/features/shared";
import { CustomInputField } from "@/features/ui";

import { getReportDatePresets } from "../utils/report-request";

// The label style of CustomInputField/CustomDatePicker, so every filter lines up.
const FIELD_LABEL_SX = {
  fontSize: "0.875rem",
  lineHeight: "1.125rem",
  transform: "scale(1)",
};

const warehouseLabel = ({ name, isActive, damagedGoodsWarehouse }) =>
  [name, damagedGoodsWarehouse ? "barang rusak" : null, isActive ? null : "nonaktif"]
    .filter(Boolean)
    .join(" · ");

const formatPeriod = ([start, end]) =>
  start?.isValid() && end?.isValid()
    ? `${formatDate(start.toDate())} – ${formatDate(end.toDate())}`
    : "Periode belum dipilih";

const ReportTitle = ({ title }) =>
  title ? (
    <Typography variant="h6" component="h1" sx={{ mr: 1, whiteSpace: "nowrap" }}>
      {title}
    </Typography>
  ) : null;

ReportTitle.propTypes = { title: PropTypes.string };

const FilterSummary = ({ title, outlet, filters, warehouses, action }) => {
  const warehouseText =
    filters.warehouseIds === null
      ? "Semua gudang"
      : warehouses
          .filter(({ id }) => filters.warehouseIds.includes(id))
          .map(({ name }) => name)
          .join(", ");
  const search = filters.search.trim();

  const chips = [
    { key: "outlet", icon: <StoreIcon />, label: outlet?.name ?? "Pilih outlet" },
    { key: "period", icon: <CalendarIcon />, label: formatPeriod(filters.dateRange) },
    {
      key: "category",
      icon: <CategoryIcon />,
      label: filters.category?.name ?? "Semua kategori",
    },
    { key: "warehouse", icon: <WarehouseIcon />, label: warehouseText },
    filters.product && {
      key: "product",
      icon: <ProductIcon />,
      label: filters.product.name,
    },
    search && { key: "search", icon: <SearchIcon />, label: `“${search}”` },
    filters.hideZeroRows && {
      key: "hideZeroRows",
      icon: <VisibilityOffIcon />,
      label: "Tanpa baris kosong",
    },
  ].filter(Boolean);

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{ flexWrap: "wrap", alignItems: "center" }}
    >
      <ReportTitle title={title} />
      {chips.map(({ key, icon, label }) => (
        <Chip key={key} size="small" icon={icon} label={label} sx={{ maxWidth: 320 }} />
      ))}
      <Stack sx={{ ml: "auto" }}>{action}</Stack>
    </Stack>
  );
};

FilterSummary.propTypes = {
  title: PropTypes.string,
  outlet: PropTypes.object,
  filters: PropTypes.object.isRequired,
  warehouses: PropTypes.array.isRequired,
  action: PropTypes.node,
};

/**
 * Report scope. While editing, every field is shown; once a report is applied the
 * panel collapses to one line of chips so the document starts near the top.
 */
export const InventoryValueReportFilters = ({
  title,
  outlet,
  onOutletChange,
  filters,
  onChange,
  options,
  errors,
  disabled,
  submitDisabled,
  onSubmit,
  collapsed,
  onExpand,
  onCollapse,
}) => {
  const warehouses = useMemo(() => options?.warehouses ?? [], [options]);
  const datePresets = useMemo(() => getReportDatePresets(), []);
  const selectedWarehouses =
    filters.warehouseIds === null
      ? []
      : warehouses.filter(({ id }) => filters.warehouseIds.includes(id));

  if (collapsed) {
    return (
      <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
        <FilterSummary
          title={title}
          outlet={outlet}
          filters={filters}
          warehouses={warehouses}
          action={
            <Button startIcon={<TuneIcon />} onClick={onExpand} disabled={disabled}>
              Ubah filter
            </Button>
          }
        />
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      sx={{ p: 2 }}
    >
      <Stack sx={{ mb: 1 }}>
        <ReportTitle title={title} />
      </Stack>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <UserOutletsSelect
            value={outlet}
            onChange={onOutletChange}
            disabled={disabled}
            TextFieldProps={{
              label: "Outlet",
              error: Boolean(errors.outlet),
              helperText: errors.outlet,
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <FormControl
            fullWidth
            variant="standard"
            sx={{ pt: 3.5 }}
            disabled={disabled}
            error={Boolean(errors.dateRange)}
          >
            <InputLabel shrink sx={FIELD_LABEL_SX}>
              Periode
            </InputLabel>
            <DateRangePicker
              value={filters.dateRange}
              disableFuture
              disabled={disabled}
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{
                field: {
                  fullWidth: true,
                  error: Boolean(errors.dateRange),
                  helperText: errors.dateRange,
                },
                shortcuts: { items: datePresets },
              }}
              format="DD/MMM/YYYY"
              localeText={{ start: "Dari", end: "Sampai" }}
              onChange={(dateRange) => onChange({ dateRange })}
            />
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <ProductCategorySelect
            type={null}
            value={filters.category}
            onChange={(category) => onChange({ category })}
            disabled={disabled}
            TextFieldProps={{
              label: "Kategori",
              InputProps: { placeholder: "Semua kategori" },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Autocomplete
            multiple
            limitTags={2}
            options={warehouses}
            value={selectedWarehouses}
            disabled={disabled || !options}
            getOptionLabel={warehouseLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) =>
              onChange({
                warehouseIds: value.length > 0 ? value.map(({ id }) => id) : null,
              })
            }
            renderInput={(params) => (
              <CustomInputField
                {...params}
                label="Gudang"
                error={Boolean(errors.warehouses)}
                helperText={errors.warehouses}
                InputProps={{
                  ...params.InputProps,
                  placeholder:
                    selectedWarehouses.length > 0 ? undefined : "Semua gudang",
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomInputField
            label="Cari barang"
            placeholder="Kode atau nama barang"
            value={filters.search}
            onChange={(event) => onChange({ search: event.target.value })}
            disabled={disabled}
            error={Boolean(errors.search)}
            helperText={errors.search}
            inputProps={{ maxLength: options?.limits.maxSearchLength ?? 100 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ProductSelect
            value={filters.product}
            onChange={(product) => onChange({ product })}
            disabled={disabled}
            TextFieldProps={{
              label: "Produk",
              InputProps: { placeholder: "Semua produk" },
            }}
          />
        </Grid>
      </Grid>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ mt: 2, alignItems: { sm: "center" } }}
      >
        <FormControlLabel
          sx={{ mr: "auto" }}
          control={
            <Switch
              checked={filters.hideZeroRows}
              onChange={(event) => onChange({ hideZeroRows: event.target.checked })}
              disabled={disabled}
            />
          }
          label="Hanya barang dengan saldo, mutasi, atau peringatan"
        />
        {onCollapse && (
          <Button startIcon={<CloseIcon />} onClick={onCollapse} disabled={disabled}>
            Batal
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          startIcon={<SearchIcon />}
          disabled={submitDisabled}
          sx={{ minWidth: 140 }}
        >
          Tampilkan
        </Button>
      </Stack>
    </Paper>
  );
};

InventoryValueReportFilters.propTypes = {
  title: PropTypes.string,
  outlet: PropTypes.object,
  onOutletChange: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    dateRange: PropTypes.array.isRequired,
    warehouseIds: PropTypes.arrayOf(PropTypes.string),
    search: PropTypes.string.isRequired,
    product: PropTypes.object,
    category: PropTypes.object,
    hideZeroRows: PropTypes.bool.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.object,
  errors: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  submitDisabled: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
  onExpand: PropTypes.func,
  onCollapse: PropTypes.func,
};
